import { memo, useMemo } from 'react';

// material-ui
import { useTheme } from '@mui/material/styles';
import { Box, Drawer, Stack, useMediaQuery, Grid, Typography } from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MenuCard from './MenuCard';
import MenuList from '../MenuList';
import LogoSection from '../LogoSection';
import MiniDrawerStyled from './MiniDrawerStyled';
import Chip from 'ui-component/extended/Chip';

import LAYOUT_CONST from 'constant';
import useConfig from 'hooks/useConfig';
import { drawerWidth } from 'store/constant';

import { useDispatch, useSelector } from 'store';
import { openDrawer } from 'store/slices/menu';
import useAuth from 'hooks/useAuth';

import { useCookies } from 'react-cookie';

// ==============================|| SIDEBAR DRAWER ||============================== //

const Sidebar = () => {
  const theme = useTheme();
  const { tokens } = useAuth();
  const matchUpMd = useMediaQuery(theme.breakpoints.up('md'));
  const matchDownMd = useMediaQuery(theme.breakpoints.down('md'));

  const dispatch = useDispatch();
  const { drawerOpen } = useSelector((state) => state.menu);

  const { layout, drawerType } = useConfig();
  const [cookies] = useCookies();

  const logo = useMemo(
    () => (
      <Box sx={{ display: 'flex', p: 2, mx: 'auto' }}>
        <LogoSection />
      </Box>
    ),
    []
  );

  // 現在のサイトのドメインを取得
  const currentOrigin = window.location.origin;

  const handleClick = () => {
    window.location.href = `${process.env.REACT_APP_HOME_URL}?foidtk=${cookies?.foidtk}&foidtk_refresh=${cookies?.foidtk_refresh}&referer=${currentOrigin}`;
  };

  const drawerContent = (
    <>
      <MenuList />
      {layout === LAYOUT_CONST.VERTICAL_LAYOUT && drawerOpen && <MenuCard />}
      {layout === LAYOUT_CONST.VERTICAL_LAYOUT && drawerOpen && (
        <Stack direction="column" justifyContent="center" alignItems="center" sx={{ mb: 2 }}>
          <Grid
            container
            alignItems="center"
            sx={{
              backgroundColor: '#008cd62b',
              borderRadius: '8px',
              padding: '10px 20px',
              boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
              cursor: 'pointer', // クリック可能であることを示す
              transition: 'background-color 0.3s, transform 0.3s', // ホバー時のアニメーション
              '&:hover': {
                backgroundColor: '#008cd600',
                transform: 'scale(1.02)' // 少し大きくなるエフェクト
              }
            }}
            mt={1}
            ml={0}
            mb={5}
            onClick={handleClick} // クリックイベント
          >
            <Grid item alignItems="center">
              <Typography
                textAlign="center"
                sx={{
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  color: '#333'
                }}
              >
                苫小牧民報電子版へ戻る
              </Typography>
            </Grid>
          </Grid>
          <Chip label="©️findout" disabled chipcolor="secondary" size="small" sx={{ cursor: 'pointer' }} />
        </Stack>
      )}
    </>
  );

  const drawerSX = {
    paddingLeft: drawerOpen ? '16px' : 0,
    paddingRight: drawerOpen ? '16px' : 0,
    marginTop: drawerOpen ? 0 : '20px'
  };

  const drawer = useMemo(
    () => (
      <>
        {matchDownMd ? (
          <Box sx={drawerSX}>{drawerContent}</Box>
        ) : (
          <PerfectScrollbar
            component="div"
            style={{
              height: !matchUpMd ? 'calc(100vh - 56px)' : 'calc(100vh - 88px)',
              ...drawerSX
            }}
          >
            {drawerContent}
          </PerfectScrollbar>
        )}
      </>
    ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [matchUpMd, drawerOpen, drawerType]
  );

  return (
    <Box component="nav" sx={{ flexShrink: { md: 0 }, width: matchUpMd ? drawerWidth : 'auto' }} aria-label="mailbox folders">
      {matchDownMd || (drawerType === LAYOUT_CONST.MINI_DRAWER && drawerOpen) ? (
        <Drawer
          variant={matchUpMd ? 'persistent' : 'temporary'}
          anchor="left"
          open={drawerOpen}
          onClose={() => dispatch(openDrawer(!drawerOpen))}
          sx={{
            '& .MuiDrawer-paper': {
              mt: matchDownMd ? 0 : 11,
              zIndex: 1099,
              width: drawerWidth,
              background: theme.palette.background.default,
              color: theme.palette.text.primary,
              borderRight: 'none'
            }
          }}
          ModalProps={{ keepMounted: true }}
          color="inherit"
        >
          {matchDownMd && logo}
          {drawer}
        </Drawer>
      ) : (
        <MiniDrawerStyled variant="permanent" open={drawerOpen}>
          {logo}
          {drawer}
        </MiniDrawerStyled>
      )}
    </Box>
  );
};

export default memo(Sidebar);
